import React, { useContext } from 'react';
import {
	DocumentRequestContextType,
	SelectedDocIndexAction,
	SelectedDocumentAction
} from './DocumentRequestContextType';
import { useFormContext } from 'react-hook-form';
import { RequestFormValidatedState } from '@interfaces/RequestForm/forms/RequestFormValidatedState';
import { useMultiFormValueSetter } from '@components/RequestForm/utilities/MultiFormAdapter/MultiFormAdapter';
const DocumentRequestContext = React.createContext<DocumentRequestContextType | null>(null);

export const useDocumentRequestContext = () => {
	const ctx = useContext(DocumentRequestContext);

	if (!ctx) {
		throw new Error('DocumentRequestContext must be used in a sub component of DocumentRequestContextProvider');
	}

	return ctx;
};

const DEFAULT_SELECTED_INDEX_ACTION: SelectedDocIndexAction = {
	id: null,
	action: SelectedDocumentAction.NONE,
};

export const DocumentRequestContextProvider: React.FC = ({ children }) => {
	const [selectedIndexAndAction, setSelectedIndexAndAction] = React.useState<SelectedDocIndexAction>(DEFAULT_SELECTED_INDEX_ACTION);
	const { getValues } = useFormContext<RequestFormValidatedState>();

	const setDocuments = useMultiFormValueSetter('documents');
	const handleCloseActionModal = React.useCallback(() => {
		setSelectedIndexAndAction({
			id: null, // Set selected to null so that modal closes
			action: SelectedDocumentAction.NONE,
		});
	}, []);

	const handleDeleteDocument = React.useCallback(() => {
		if (!selectedIndexAndAction.id) return; // No document to delete - modal shouldn't even be open

		const currentDocuments = getValues().documents || {};

		const updatedDocuments = { ...currentDocuments };
		delete updatedDocuments[selectedIndexAndAction.id];

		setDocuments(updatedDocuments);
		handleCloseActionModal();
	}, [getValues, handleCloseActionModal, selectedIndexAndAction.id, setDocuments]);

	const selectedDocument = React.useMemo(() => {
		const currentDocuments = getValues().documents || {};
		if (!selectedIndexAndAction.id) return;

		return currentDocuments[selectedIndexAndAction.id];
	}, [getValues, selectedIndexAndAction.id]);

	return (
		<DocumentRequestContext.Provider value={{
			selectedDocument,
			handleDeleteDocument,
			handleCloseActionModal,
			selectedIndexAndAction,
			setSelectedIndexAndAction,
		}}>
			{children}
		</DocumentRequestContext.Provider>
	);
};
